import React, { useCallback, useEffect, useMemo, useState } from 'react';
import '@/assets/app/css/adventure.css';
import trophyIcon from '@/assets/app/images/icon/icon-trophy.png';
import flagIcon from '@/assets/app/images/icon/icon-flag.png';
import starIcon from '@/assets/app/images/icon/icon-star.png';
import starCircleIcon from '@/assets/app/images/icon/icon-status-001.png';
import circleIcon from '@/assets/app/images/icon/icon-status-002.png';
import lockedIcon from '@/assets/app/images/icon/icon-locked.png';
import arrowDownIcon from '@/assets/app/images/icon/icon-arrow-down.png';
import defaultPersonImage from '@/assets/app/images/common/default-person.svg';
import starImage from '@/assets/app/images/adventure/all_clear_star.png';
import recordIcon from '@/assets/app/images/adventure/record.png';
import useBottomSheetStore from '@/shared/store/UseBottomSheetStore';
import { getUploadsUrl, setThemeColor, vAppAjax } from '@/shared/utils/CommonUtil';
import { useModal } from '@/shared/components/ModalProvider';
import logger from '@/shared/utils/Logger';
import Chapter from './Chapter';
import AppButton from '@/shared/components/ui/new/AppButton';
import useAppStore from '@/shared/store/UseAppStore';
import useAppAuthStore from '@/shared/store/UseAppAuthStore';

interface PersonCard {
  personId: number;
  personName: string;
  personImage: string;
  eraName: string;
  chapters: ChapterData[];
  cardIndex: number; // 같은 인물의 몇 번째 카드인지
  totalCards: number; // 같은 인물의 총 카드 수
}

interface ProcessedData {
  cards: PersonCard[];
  allPersons: Set<number>;
}

const CHAPTERS_PER_CARD = 5;

interface Character {
  id: number;
  name: string;
  avatar: string;
  score: number;
  maxScore: number;
  stars: number;
  status: number;
  isLocked?: boolean;
  achievementIcon?: 'trophy' | 'flag' | null;
}

interface AdventureProps {
  selectedDynasty?: string;
  onDynastyChange?: (dynasty: string) => void;
  onCharacterSelect?: (characterId: number) => void;
  characters?: Character[];
}

const Adventure: React.FC<AdventureProps> = () => {
  const modal = useModal();
  const { setIsShowBottomNavigation, setIsShowProfileHeader } = useAppStore();
  const [eraCategoryList, setEraCategoryList] = useState<EraCategory[]>([]);
  const [selectedEraCategory, setSelectedEraCategory] = useState<EraCategory>();
  const [chapterList, setChapterList] = useState<ChapterData[]>([]);
  const [showChapter, setShowChapter] = useState<boolean>(false);
  const [currentChapter, setCurrentChapter] = useState<ChapterData>();
  const [viewingResult, setViewingResult] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdventureComplete, setIsAdventureComplete] = useState<boolean>(false);
  const [showCompletionScreen, setShowCompletionScreen] = useState<boolean>(false);
  const { openSheet } = useBottomSheetStore();
  const { member, updateMember } = useAppAuthStore();
  const isTester = !!member?.isTester;

  useEffect(() => {
    setIsShowBottomNavigation(true);
    setIsShowProfileHeader(true);
    loadEraList();
    loadInitData();
    loadCompletionStatus();
  }, []);

  useEffect(() => {
    loadChapterList();
    if (selectedEraCategory) {
      setThemeColor(selectedEraCategory.themeColor);
    }
  }, [selectedEraCategory]);

  const processedData = useMemo((): ProcessedData => {
    if (!chapterList || chapterList.length === 0) {
      return { cards: [], allPersons: new Set() };
    }

    // personId별로 그룹화
    const personMap = new Map<number, ChapterData[]>();

    chapterList.forEach(chapter => {
      if (!personMap.has(chapter.personId)) {
        personMap.set(chapter.personId, []);
      }
      personMap.get(chapter.personId)!.push(chapter);
    });

    const cards: PersonCard[] = [];
    const allPersons = new Set<number>();

    // 각 인물의 챕터를 5개씩 나눠서 카드 생성
    personMap.forEach((chapters, personId) => {
      allPersons.add(personId);

      // 챕터 번호순으로 정렬
      const sortedChapters = [...chapters].sort((a, b) => a.chapterNumber - b.chapterNumber);

      // 5개씩 나누기
      const totalCards = Math.ceil(sortedChapters.length / CHAPTERS_PER_CARD);

      for (let i = 0; i < totalCards; i++) {
        const startIdx = i * CHAPTERS_PER_CARD;
        const endIdx = Math.min(startIdx + CHAPTERS_PER_CARD, sortedChapters.length);
        const cardChapters = sortedChapters.slice(startIdx, endIdx);

        cards.push({
          personId,
          personName: chapters[0].personName,
          personImage: chapters[0].personThumbnailImage,
          eraName: chapters[0].eraName,
          chapters: cardChapters,
          cardIndex: i,
          totalCards,
        });
      }
    });

    return { cards, allPersons };
  }, [chapterList]);

  // 이전 인물들이 모두 완료되었는지 확인
  const isPreviousPersonsCompleted = (currentPersonId: number): boolean => {
    if (!chapterList || chapterList.length === 0) return true;

    // 현재 인물의 첫 번째 챕터 인덱스 찾기
    const currentPersonFirstIndex = chapterList.findIndex(c => c.personId === currentPersonId);
    if (currentPersonFirstIndex === -1) return true;

    // 이전 인덱스의 챕터들만 확인
    const previousChapters = chapterList.slice(0, currentPersonFirstIndex);
    if (previousChapters.length === 0) return true;

    // 이전 인물들의 ID 수집
    const previousPersonIds = new Set(previousChapters.map(c => c.personId));

    // 이전 인물들의 모든 챕터가 완료되었는지 확인
    for (const personId of previousPersonIds) {
      const personChapters = chapterList.filter(c => c.personId === personId);
      const allCompleted = personChapters.every(c => c.isCompleted);

      if (!allCompleted) {
        return false;
      }
    }

    return true;
  };

  const checkLimit = async (chapter: ChapterData) => {
    setViewingResult(false);
    try {
      // setLoading(true);
      await vAppAjax(
        'get',
        'daily-play/check',
        {},
        (response: AxiosResponse<CommonResult<DailyPlayCheckResponse>>) => {
          const resultData = response.data;
          if (resultData.code === 200) {
            const result = resultData.result;

            if (isTester) {
              setShowChapter(true);
              setCurrentChapter(chapter);
              return;
            }

            if (result.playCount === 0) {
              setShowChapter(true);
              setCurrentChapter(chapter);
            } else if (result.playCount === 1) {
              openSheet('star-point-consume', {
                points: result.extraPlayPoints,
                reward: member?.totalPoints || 0,
                onConfirm: () => {
                  consumeExtraPlayPoints(chapter);
                },
              });
            } else {
              openSheet('message', {
                type: 'ALERT',
                message: '오늘의 플레이 횟수를 모두 사용했습니다 <br /> 내일 다시 도전해주세요!',
                messageFontSize: 19,
              });
            }
          } else {
            console.warn(resultData.message);
          }
        },
        (error: any) => {
          logger.error(error);
        },
      );
    } catch (error: any) {
      console.error('플레이 횟수 확인 실패:', error);
    } finally {
      // setLoading(false);
    }
  };

  // 다시하기(추가 플레이) 별 포인트 차감
  const consumeExtraPlayPoints = async (chapter: ChapterData) => {
    await vAppAjax(
      'post',
      'daily-play/consume-extra-play',
      {},
      (response: AxiosResponse<CommonResult<{ totalPoints: number }>>) => {
        const resultData = response.data;
        if (resultData.code === 200) {
          updateMember({ totalPoints: resultData.result.totalPoints });
          setShowChapter(true);
          setCurrentChapter(chapter);
        } else {
          openSheet('message', { type: 'ALERT', message: resultData.message });
        }
      },
      (error: any) => {
        logger.error(error);
        openSheet('message', {
          type: 'ALERT',
          message: '별 포인트가 부족하여 다시 플레이할 수 없어요.',
        });
      },
    );
  };

  // 카드의 상태 결정 (trophy, flag, locked)
  const getCardStatus = (card: PersonCard): number => {
    const previousCompleted = isPreviousPersonsCompleted(card.personId);

    // 이전 인물이 완료되지 않았으면 locked
    if (!previousCompleted) {
      return 0; // locked
    }

    // 현재 카드의 모든 챕터가 완료되었는지 확인
    const allCompleted = card.chapters.every(c => c.isCompleted);

    if (allCompleted) {
      return 2; // trophy
    }

    return 1; // flag (현재 플레이 중)
  };

  const goChapter = (chapter: ChapterData, status: string) => {
    if (isTester) {
      checkLimit(chapter);
      return;
    }
    if (status === 'play') {
      /* @TODO 임시 수정*/
      checkLimit(chapter);
    } else {
      openSheet('message', {
        type: 'ALERT',
        message:
          '<span style="color:#0094EF">모험중인 챕터를 완료해야 <br /> 다음 챕터로 넘어갈 수 있어요</span>',
        onConfirmTitle: '돌아가기',
      });
      return;
    }
  };

  // 별 렌더링 (최대 5개)
  const renderStars = (card: PersonCard) => {
    const status = getCardStatus(card);
    const isLocked = !isTester && status === 0;

    if (isLocked) {
      return (
        <div className="status locked-stars">
          {card.chapters.map((chapter, index) => (
            <div
              key={index}
              className="circle bg-[#9D9D9D]"
              onClick={() => goChapter(chapter, 'locked')}
            >
              <img className="icon-img" src={circleIcon} />
            </div>
          ))}
        </div>
      );
    }

    // 현재 카드의 챕터 상태에 따라 별 표시
    const stars = card.chapters.map((chapter, index) => {
      if (chapter.isCompleted) {
        // 완료된 챕터 - 클릭하면 완료했던 결과를 다시 볼 수 있음
        return (
          <div
            key={`completed-${index}`}
            className="circle theme-color"
            onClick={() => goChapterResult(chapter)}
          >
            <img className="icon-img" src={starCircleIcon} />
          </div>
        );
      } else {
        // 완료되지 않은 챕터 중 첫 번째는 현재 플레이할 챕터
        const isCurrentChapter = card.chapters.slice(0, index).every(c => c.isCompleted);

        if (isCurrentChapter) {
          return (
            <div
              key={`current-${index}`}
              className="circle theme-color"
              onClick={() => goChapter(chapter, 'play')}
            >
              <img className="icon-img" src={circleIcon} />
            </div>
          );
        } else {
          return (
            <div
              key={`locked-${index}`}
              className="circle bg-[#9D9D9D]"
              onClick={() => goChapter(chapter, 'locked')}
            >
              <img className="icon-img" src={circleIcon} />
            </div>
          );
        }
      }
    });

    // 5개가 안 되면 나머지는 회색으로 채우기
    // const remainingStars = CHAPTERS_PER_CARD - card.chapters.length;
    // if (remainingStars > 0) {
    //   for (let i = 0; i < remainingStars; i++) {
    //     stars.push(
    //       <div key={`empty-${i}`} className="circle">
    //         <img src={grayCircleIcon} />
    //       </div>,
    //     );
    //   }
    // }

    return <div className="status">{stars}</div>;
  };

  // 카드 클릭 핸들러
  const handleCardClick = (card: PersonCard) => {
    const status = getCardStatus(card);

    if (status === 0) {
      // locked 상태면 클릭 불가
      console.log('이전 인물을 먼저 완료해주세요.');
      return;
    }

    // 현재 플레이할 챕터 찾기
    const currentChapter = card.chapters.find(c => !c.isCompleted);

    if (currentChapter) {
      console.log('Navigate to chapter:', currentChapter);
      // 챕터 플레이 페이지로 이동
      // navigate(`/chapter/${currentChapter.chapterId}`);
    } else {
      // 모든 챕터가 완료된 경우
      console.log('All chapters completed for this card');
    }
  };

  const renderAchievementIcon = (status: number) => {
    if (status === null || status === undefined) return null;
    let icon: string;
    let iconImage: string;
    const isLocked = !isTester;

    switch (status) {
      case 1:
        icon = 'flag';
        iconImage = flagIcon;
        break;
      case 2:
        icon = 'trophy';
        iconImage = trophyIcon;
        break;
      default:
        icon = 'locked';
        iconImage = lockedIcon;
    }

    if (!isLocked && icon === 'locked') {
      return;
    }

    return (
      <div className={`achievement-icon ${icon}-icon`}>
        <img src={iconImage} />
      </div>
    );
  };

  // 점수 계산
  const getCardScore = (card: PersonCard) => {
    // questionCount를 모두 합산
    const total = card.chapters.reduce((sum, chapter) => sum + chapter.questionCount, 0);

    // point를 모두 합산
    const completed = card.chapters.reduce((sum, chapter) => sum + chapter.point, 0);

    return { completed, total };
  };

  const selectEra = () => {
    openSheet('select-era-list', {
      title: '시대선택',
      eraCategoryList: eraCategoryList,
      selectedEraCategory,
      setSelectedEraCategory,
      showHeader: true,
      position: 'center',
    });
  };

  const loadEraList = async () => {
    await vAppAjax(
      'get',
      'adventure/era-category',
      {},
      (response: AxiosResponse<CommonResult<EraCategory[]>>) => {
        const resultData = response.data;
        if (resultData.code === 200) {
          setEraCategoryList(resultData.result);
        } else {
          console.warn(resultData.message);
        }
      },
      (error: any) => {
        logger.error(error);
      },
    );
  };

  const loadInitData = async () => {
    await vAppAjax(
      'get',
      'adventure/latest-category',
      {},
      (response: AxiosResponse<CommonResult<EraCategory>>) => {
        const resultData = response.data;
        if (resultData.code === 200) {
          setSelectedEraCategory(resultData.result);
          //   modal.info(`${isEdit ? '수정되었습니다.' : '등록되었습니다.'}`, () => {
          //     windowInfo?.onConfirm?.();
          //     closeWindow(windowInfo!.id);
          //   });
          // } else {
          //   modal.warn(resultData.message);
        } else {
          console.warn(resultData.message);
        }
      },
      (error: any) => {
        modal.error('저장 중 오류가 발생했습니다.');
        logger.error(error);
      },
    );
  };

  // 전체 모험(모든 시대) 완료 여부 조회
  const loadCompletionStatus = async () => {
    await vAppAjax(
      'get',
      'adventure/completion-status',
      {},
      (response: AxiosResponse<CommonResult<{ completed: boolean }>>) => {
        const resultData = response.data;
        if (resultData.code === 200) {
          setIsAdventureComplete(resultData.result.completed);
          setShowCompletionScreen(resultData.result.completed);
        } else {
          console.warn(resultData.message);
        }
      },
      (error: any) => {
        logger.error(error);
      },
    );
  };

  // 완료된 챕터의 결과 다시보기
  const goChapterResult = (chapter: ChapterData) => {
    setViewingResult(true);
    setShowChapter(true);
    setCurrentChapter(chapter);
  };

  const loadChapterList = async () => {
    try {
      setLoading(true);
      setError(null);
      await vAppAjax(
        'get',
        'adventure/chapter',
        { eraCategoryId: selectedEraCategory?.eraCategoryId },
        (response: AxiosResponse<CommonResult>) => {
          const resultData = response.data;
          if (resultData.code === 200) {
            setChapterList(resultData.result);
            setLoading(false);
          } else {
            console.warn(resultData.message);
          }
        },
        (error: any) => {
          modal.error('저장 중 오류가 발생했습니다.');
          logger.error(error);
        },
      );
    } catch (err) {
      console.error('Failed to fetch chapter list:', err);
      setError('챕터 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  if (loading && chapterList.length === 0) {
    return <></>;
  }

  return (
    <>
      <div className="dynasty-selection">
        <div className="dynasty-header flex items-center theme-color-btn p-1">
          <button className="dynasty-title f-one-pop text-white font-bold px-4 text-[20px]">
            <span>{selectedEraCategory?.categoryName}</span>
          </button>

          <div className="h-6 border-l border-white/40 mx-2"></div>

          <button
            className="era-selector flex items-center justify-between gap-1 shrink-0 text-white text-[16px] pl-2 pr-4 min-w-[95px]"
            onClick={() => selectEra()}
          >
            <span className="whitespace-nowrap">시대선택</span>
            <span className="dropdown-icon shrink-0">
              <img src={arrowDownIcon} alt="arrow" />
            </span>
          </button>
        </div>
        {isAdventureComplete && showCompletionScreen ? (
          <div className="flex flex-col items-center text-center px-6 pt-10 pb-10">
            <img src={starImage} alt="완료" style={{ width: 260, maxWidth: '100%' }} />
            <div className="text-[24px] font-bold mt-6 leading-snug text-[#F3A9C9]">
              정말 대단해요!
              <br />
              전체 모험을 완료했어요
            </div>
            <div className="text-[14px] text-[#9D9D9D] mt-4 leading-relaxed">
              <img src={recordIcon} alt="기록" className="inline-block w-4 h-4 align-middle mr-1" />
              기록아이콘을 누르면
              <br />
              나의 업적을 확인할 수 있어요
            </div>
            <div className="w-full mt-8">
              <AppButton
                className="f-one-pop text-[20px]"
                backgroundColor="#F3A9C9"
                boxShadowColor="#E27DA4"
                onConfirm={() => setShowCompletionScreen(false)}
                message="다시 도전하기"
              />
            </div>
          </div>
        ) : (
          <div className="characters-list">
            {processedData.cards.map(card => {
              const status = getCardStatus(card);
              const isLocked = !isTester && status === 0;
              const score = getCardScore(card);

              return (
                <div
                  key={`${card.personId}-${card.cardIndex}`}
                  className={`character-card ${isLocked ? 'locked' : ''}`}
                  // onClick={() => handleCardClick(card)}
                >
                  <div className="character-left">
                    {renderAchievementIcon(status)}
                    <div className="character-avatar">
                      <img
                        src={
                          card.personImage
                            ? `${getUploadsUrl()}/person/${card.personImage}`
                            : defaultPersonImage
                        }
                        alt={card.personName}
                      />

                      {isLocked && <div className={`${isLocked ? 'locked-avatar' : ''}`} />}
                    </div>
                  </div>

                  <div className={`character-info ${isLocked ? 'locked' : ''}`}>
                    {
                      <div className="character-header">
                        <span className="character-name">
                          {isLocked ? `　　　` : card.personName}
                          {card.totalCards > 1 && ` (${card.cardIndex + 1})`}
                        </span>
                        <span className="character-score">
                          <img src={starIcon} alt="star" /> {isLocked ? `　` : score.completed}/
                          {isLocked ? `　` : score.total}
                        </span>
                      </div>
                    }
                    {renderStars(card)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {showChapter && (
        <div style={{ position: 'absolute', top: 0, zIndex: 10, width: '100%', height: '100%' }}>
          <Chapter
            chapter={currentChapter}
            onBack={() => {
              setShowChapter(false);
              setViewingResult(false);
            }}
            viewResultOnly={viewingResult}
          />
        </div>
      )}
    </>
  );
};

export default Adventure;
